import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Alert,
  Snackbar,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableSortLabel,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Repeat as RepeatIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as CartIcon,
  Search as SearchIcon,
  Upload as FileUploadIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { guestBlogSiteService } from '../services/guestBlogSiteService';
import { publisherService } from '../services/publisherService';
import { clientService } from '../services/clientService';
import { guestBlogOrderService } from '../services/guestBlogOrderService';
import { projectService } from '../services/projectService';
import { cartService } from '../services/cartService';
import { clientPlanService } from '../services/clientPlanService';
import { useAuth } from '../contexts/AuthContext';
import { formatRoundedPrice } from '../utils/priceRounding';
import {
  GuestBlogSite,
  GuestBlogSiteStatus,
  COUNTRIES,
  isGuestBlogSiteHealthOk,
  guestBlogSiteHealthTooltip,
} from '../types/guestBlogSite';
import { categoryService } from '../services/categoryService';
import GuestBlogSiteForm, { clearGuestBlogSiteAddDraft } from '../components/guestBlogSites/GuestBlogSiteForm';
import BulkUploadModal from '../components/guestBlogSites/BulkUploadModal';
import CartItemForm from '../components/guestBlogSites/CartItemForm';
import ProjectContextBanner from '../components/cart/ProjectContextBanner';
import CartSidebar from '../components/cart/CartSidebar';
import CheckoutModal from '../components/cart/CheckoutModal';
import AllCartDrawer from '../components/cart/AllCartDrawer';
import RequestPriceChangeModal from '../components/priceChangeRequests/RequestPriceChangeModal';
import { priceChangeRequestService, PriceChangeRequest } from '../services/priceChangeRequestService';
import { useCart } from '../contexts/CartContext';
import { useProjectWorkflowOptional } from '../contexts/ProjectWorkflowContext';
import ColumnVisibilityMenu from '../components/common/ColumnVisibilityMenu';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import DocumentPreviewModal from '../components/common/DocumentPreviewModal';
import { localDayBoundaryIso } from '../utils/dateRange';

interface GuestBlogSitesPageState {
  page: number;
  rowsPerPage: number;
  search: string;
  category: string;
  country: string;
  status: GuestBlogSiteStatus | '';
  publisherId: string;
  outreachEmail: string;
  clientId: string;
  projectId: string;
  planId: string;
  minDr: string;
  maxDr: string;
  minTraffic: string;
  maxTraffic: string;
  minPrice: string;
  maxPrice: string;
  fromDate: string;
  toDate: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface CartItem {
  id: string;
  guestBlogSiteId: string;
  guestBlogSiteUrl: string;
  clientId: string;
  clientName: string;
  projectId: string;
  projectName: string;
  adjustedPrice: number;
  submissionType: 'GBS' | 'LI' | 'BOTH';
  contentText?: string;
  contentDocUrl?: string;
  orderDate?: string; // ISO date string
}

// Embedded CSS styles matching the provided HTML design
const styles = `
  .sites-filter-bar {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0,0,0,.05);
    padding: 12px 14px;
    margin-bottom: 10px;
  }
  .sites-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }
  .sites-filter-row:last-child {
    margin-bottom: 0;
  }
  .filter-search {
    display: flex;
    align-items: center;
    gap: 7px;
    background: #F1F5F9;
    border: 1px solid #E2E8F0;
    border-radius: 6px;
    padding: 6px 10px;
    min-width: 210px;
    transition: all 0.15s;
  }
  .filter-search:focus-within {
    background: #FFFFFF;
    border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  .filter-search svg {
    width: 13px;
    height: 13px;
    color: #94A3B8;
    flex-shrink: 0;
  }
  .filter-search input {
    border: none;
    background: none;
    outline: none;
    font-family: inherit;
    font-size: 13px;
    color: #0F172A;
    width: 100%;
  }
  .filter-search input::placeholder {
    color: #94A3B8;
  }
  .filter-divider {
    width: 1px;
    height: 18px;
    background: #E2E8F0;
    flex-shrink: 0;
  }
  .filter-group {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 500;
    color: #94A3B8;
    white-space: nowrap;
    letter-spacing: 0.3px;
  }
  .filter-input {
    border: 1px solid #E2E8F0;
    border-radius: 6px;
    padding: 5px 8px;
    font-family: inherit;
    font-size: 12.5px;
    color: #475569;
    background: #F1F5F9;
    outline: none;
    transition: all 0.15s;
    width: 72px;
  }
  .filter-input:focus {
    border-color: #2563EB;
    background: #FFFFFF;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  .filter-select {
    appearance: none;
    border: 1px solid #E2E8F0;
    border-radius: 6px;
    padding: 5px 26px 5px 9px;
    font-family: inherit;
    font-size: 12.5px;
    color: #475569;
    background: #F1F5F9;
    outline: none;
    cursor: pointer;
    transition: all 0.15s;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 7px center;
    min-width: 130px;
  }
  .filter-select:focus {
    border-color: #2563EB;
    background-color: #FFFFFF;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  .filter-select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .filter-spacer {
    flex: 1;
  }
  .more-filters-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: #2563EB;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 4px 2px;
    transition: opacity 0.15s;
  }
  .more-filters-btn:hover {
    opacity: 0.75;
  }
  .more-filters-btn svg {
    width: 12px;
    height: 12px;
    transition: transform 0.2s;
  }
  .more-filters-btn.open svg {
    transform: rotate(180deg);
  }
  .more-filters-panel {
    display: none;
    padding-top: 8px;
    border-top: 1px solid #F1F5F9;
    margin-top: 8px;
  }
  .more-filters-panel.open {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .date-input {
    border: 1px solid #E2E8F0;
    border-radius: 6px;
    padding: 5px 8px;
    font-family: inherit;
    font-size: 12.5px;
    color: #475569;
    background: #F1F5F9;
    outline: none;
    transition: all 0.15s;
  }
  .date-input:focus {
    border-color: #2563EB;
    background: #FFFFFF;
  }
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 13px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.15s;
    white-space: nowrap;
    line-height: 1;
  }
  .btn svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }
  .btn-primary {
    background: #2563EB;
    color: #fff;
    box-shadow: 0 1px 2px rgba(37,99,235,0.2);
  }
  .btn-primary:hover {
    background: #1D4ED8;
    box-shadow: 0 2px 6px rgba(37,99,235,0.25);
  }
  .btn-primary:disabled {
    background: #CBD5E1;
    cursor: not-allowed;
    box-shadow: none;
  }
  .btn-secondary {
    background: #FFFFFF;
    color: #475569;
    border: 1px solid #E2E8F0;
    box-shadow: 0 1px 2px rgba(0,0,0,.05);
  }
  .btn-secondary:hover {
    background: #F1F5F9;
    color: #0F172A;
  }
  .btn-secondary:disabled {
    background: #F8FAFC;
    color: #CBD5E1;
    cursor: not-allowed;
  }
  .btn-sm {
    padding: 5px 10px;
    font-size: 11.5px;
  }
  .filter-row-2 {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    row-gap: 10px;
    padding-top: 2px;
    width: 100%;
    min-width: 0;
  }
  .filter-row-2 .filter-select {
    min-width: 160px;
    padding-top: 6px;
    padding-bottom: 6px;
    font-size: 13px;
  }
  .filter-clear-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #94A3B8;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 4px 6px;
    border-radius: 6px;
    transition: all 0.15s;
  }
  .filter-clear-btn:hover {
    background: #FEF2F2;
    color: #DC2626;
  }
  .filter-clear-btn svg {
    width: 11px;
    height: 11px;
  }
  
  /* Sites Table Styles */
  .sites-table-container {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0,0,0,.05);
    overflow-x: auto;
  }
  .sites-table-wrapper {
    min-width: 100%;
    overflow-x: auto;
  }
  .sites-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .sites-table th {
    background: #EEF2FF;
    padding: 12px 16px;
    text-align: left;
    font-weight: 700;
    font-size: 12px;
    color: #4338CA;
    border-bottom: 2px solid #C7D2FE;
    white-space: nowrap;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .sites-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.15s;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
  }
  .sites-table th.sortable .sort-icon {
    vertical-align: middle;
  }
  .sites-table th.sortable:hover {
    background: #E0E7FF;
  }
  .sites-table th .sort-icon {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    margin-left: 4px;
    gap: 0;
    line-height: 1;
  }
  .sites-table th .sort-icon svg {
    width: 14px;
    height: 14px;
    color: #CBD5E1;
    transition: color 0.15s;
  }
  .sites-table th.sortable:hover .sort-icon svg {
    color: #94A3B8;
  }
  .sites-table th .sort-icon svg.active {
    color: #2563EB;
  }
  .sites-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #F1F5F9;
    vertical-align: middle;
  }
  .sites-table tr:hover td {
    background: #F8FAFC;
  }
  .sites-table tr:last-child td {
    border-bottom: none;
  }
  .sites-table .checkbox-cell {
    width: 50px;
    text-align: center;
  }
  .sites-table .actions-cell {
    width: 100px;
    text-align: center;
  }
  .site-url-cell {
    min-width: 200px;
    max-width: 300px;
  }
  .site-url-link {
    color: #2563EB;
    text-decoration: none;
    font-weight: 500;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .site-url-link:hover {
    text-decoration: underline;
  }
  .site-badges {
    display: flex;
    gap: 4px;
    margin-top: 4px;
    flex-wrap: wrap;
  }
  .site-badge {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.2;
  }
  .site-badge.in-plan {
    background: #DCFCE7;
    color: #166534;
  }
  .site-badge.ordered {
    background: #FEF3C7;
    color: #92400E;
  }
  .site-badge.override {
    background: #FEF3C7;
    color: #92400E;
  }
  .price-cell {
    font-weight: 500;
    color: #1E293B;
  }
  .price-display {
    color: #2563EB;
  }
  .price-secondary {
    color: #7C3AED;
  }
  .status-chip {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    display: inline-block;
  }
  .status-chip.active {
    background: #DCFCE7;
    color: #166534;
  }
  .status-chip.inactive {
    background: #F1F5F9;
    color: #64748B;
  }
  .usage-chip {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .usage-chip.low {
    background: #DCFCE7;
    color: #166534;
  }
  .usage-chip.medium {
    background: #FEF3C7;
    color: #92400E;
  }
  .usage-chip.high {
    background: #FEE2E2;
    color: #991B1B;
  }
  .usage-chip.zero {
    background: #F1F5F9;
    color: #64748B;
  }
  .cart-btn {
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #E2E8F0;
    background: #FFFFFF;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    font-family: inherit;
  }
  .cart-btn:hover:not(:disabled) {
    background: #FFFFFF;
    border-color: #94A3B8;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  .cart-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .cart-btn svg {
    width: 16px;
    height: 16px;
    color: #2563EB;
  }
  .action-btn {
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #E2E8F0;
    background: #FFFFFF;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    font-family: inherit;
  }
  .action-btn:hover {
    background: #FFFFFF;
    border-color: #94A3B8;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  .action-btn.delete:hover {
    background: #FFFFFF;
    border-color: #F87171;
    color: #DC2626;
  }
  .action-btn.archive:hover {
    background: #FFFFFF;
    border-color: #FBBF24;
    color: #D97706;
  }
  .action-btn svg {
    width: 16px;
    height: 16px;
    color: #64748B;
  }
  .action-btn.delete svg {
    color: #EF4444;
  }
  .action-btn.archive svg {
    color: #D97706;
  }
  .price-change-btn {
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #E2E8F0;
    background: #FFFFFF;
    color: #2563EB;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    font-family: inherit;
  }
  .price-change-btn:hover {
    background: #FFFFFF;
    border-color: #94A3B8;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  .price-change-btn.rejected {
    color: #DC2626;
  }
  .price-change-btn.rejected:hover {
    border-color: #F87171;
  }
  .price-change-btn svg {
    width: 16px;
    height: 16px;
  }
  .price-change-status {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    text-align: center;
  }
  .price-change-status.pending {
    background: #FEF3C7;
    color: #92400E;
  }
  .price-change-status.approved {
    background: #DCFCE7;
    color: #166534;
  }
  .price-change-status.rejected {
    background: #FEE2E2;
    color: #991B1B;
  }
  .loading-row, .empty-row {
    text-align: center;
    padding: 40px 16px;
    color: #64748B;
    font-style: italic;
  }
  
  /* Pagination Styles */
  .pagination-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
    padding: 12px 0;
  }
  .pagination-info {
    color: #64748B;
    font-size: 12px;
  }
  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .pagination-btn {
    padding: 0;
    border: 1px solid #E2E8F0;
    background: #FFFFFF;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    font-size: 12px;
    color: #64748B;
  }
  .pagination-btn svg {
    width: 14px;
    height: 14px;
  }
  .pagination-btn:hover:not(:disabled) {
    background: #F8FAFC;
    border-color: #CBD5E1;
  }
  .pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: #F8FAFC;
  }
  .pagination-btn.active {
    background: #2563EB;
    color: white;
    border-color: #2563EB;
  }
  .pagination-rows {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #64748B;
    font-size: 12px;
  }
  .pagination-rows select {
    border: 1px solid #E2E8F0;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    background: #FFFFFF;
    cursor: pointer;
    outline: none;
    transition: all 0.15s;
    min-width: 60px;
  }
  .pagination-rows select:focus {
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37,99,235,0.1);
  }
  .pagination-ellipsis {
    padding: 0 4px;
    color: #94A3B8;
    font-size: 12px;
  }
  
  /* Page Header Styles */
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1E293B;
    margin: 0;
  }
  .cart-icon-btn {
    position: relative;
    padding: 10px;
    border-radius: 8px;
    border: none;
    background: #2563EB;
    color: white;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .cart-icon-btn:hover {
    background: #1D4ED8;
  }
  .cart-icon-btn svg {
    width: 20px;
    height: 20px;
  }
  .cart-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    background: #EF4444;
    color: white;
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 600;
    min-width: 18px;
    text-align: center;
  }
`;

/** Optimistically bump client-wide monthly usage in every cached sites query. */
function bumpUsageCountInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  siteId: string,
  delta: number
) {
  queryClient.setQueriesData({ queryKey: ['guestBlogSites'] }, (old: any) => {
    if (!old?.data) return old;
    return {
      ...old,
      data: old.data.map((site: any) =>
        site.id === siteId && site.usageStats
          ? {
              ...site,
              usageStats: {
                ...site.usageStats,
                monthlyCount: Math.max(0, (site.usageStats.monthlyCount || 0) + delta),
              },
            }
          : site
      ),
    };
  });
}

const GuestBlogSitesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get category filter from navigation state (from Dashboard)
  const navigationState = location.state as { categoryFilter?: string } | null;
  const categoryFromDashboard = navigationState?.categoryFilter || '';
  
  // Get URL parameters for project context and selection mode
  const searchParams = new URLSearchParams(location.search);
  const urlProjectId = searchParams.get('projectId') || '';
  const urlClientId = searchParams.get('clientId') || '';
  const urlPlanId = searchParams.get('planId') || '';
  const selectionMode = searchParams.get('mode') === 'select';
  const selectionPlanId = searchParams.get('planId') || '';
  const selectionClientId = searchParams.get('clientId') || '';
  const fromPlanning = searchParams.get('from') === 'planning';
  const fromAISuggestions = searchParams.get('from') === 'ai-suggestions';
  const paymentStatus = searchParams.get('paymentStatus') || '';
  const paymentAttemptId = searchParams.get('paymentAttemptId') || '';
  const stripeSessionId = searchParams.get('session_id') || '';
  const paymentCancelled = searchParams.get('paymentCancelled') === '1';
  const selectedSiteIdsParam = searchParams.get('selectedSiteIds') || '';
  const selectedSiteIdsSet = new Set(
    selectedSiteIdsParam
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
  );

  /** Deep-link from Requested sites (and bookmarks): pre-fill site search */
  const urlSearchFromQuery = searchParams.get('search') || searchParams.get('domain') || '';
  
  // Cart hooks - use project from URL for cart context
  const {
    cart: cartData,
    cartCount,
    cartTotal,
    fetchCart,
    addToCart,
    removeFromCart,
  } = useCart(urlProjectId);
  
  // Project context banner is hidden in the project workflow (context shown by the workflow shell).
  const SHOW_PROJECT_CONTEXT_BANNER: boolean = false;

  // Project workflow (Explore Sites step) — used to show the "X sites added — Go to plan" summary.
  const workflow = useProjectWorkflowOptional();

  // Cart UI state
  const [cartSidebarOpen, setCartSidebarOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [allCartDrawerOpen, setAllCartDrawerOpen] = useState(false);
  
  // State for all cart items (when no project context)
  const [allCartData, setAllCartData] = useState<any>(null);
  const [allCartLoading, setAllCartLoading] = useState(false);
  
  // State for checkout from AllCartDrawer
  const [checkoutProjectId, setCheckoutProjectId] = useState<string>('');
  const [checkoutProjectName, setCheckoutProjectName] = useState<string>('');
  const [checkoutClientName, setCheckoutClientName] = useState<string>('');
  const [paymentFinalizing, setPaymentFinalizing] = useState(false);
  
  // Get user role from auth context - MUST be before column visibility
  const { user, isAdmin, isSuperAdmin, isAgency, isClient, hasPermission } = useAuth();
  
  const isSuperAdminFallback = user?.role === 'SUPER_ADMIN' || user?.email === 'superadmin@example.com';
  const isAdminFallback = isAdmin || isSuperAdminFallback;
  const canSite = (key: string) => isSuperAdminFallback || hasPermission(key);
  const adminCanFilterPublisher = canSite('sites.filter.publisher');
  const adminCanFilterOutreach = canSite('sites.filter.outreach');
  const adminCanFilterStatus = canSite('sites.filter.status');
  const adminCanFilterDateAdded = canSite('sites.filter.date_added');
  const adminCanFilterClientPricing = canSite('sites.filter.client_pricing');
  const isPublisher = user?.role === 'PUBLISHER';
  const canUseProjectCart = isAdminFallback || isClient || isAgency;
  const usesPaymentCheckout = isClient || isAgency;
  const inWorkflow = Boolean(workflow && urlProjectId && !isPublisher);

  // Standalone "Guest Blog Sites" menu (main sidebar) for admin / super admin:
  // pure browse view of the site catalog. No project context → hide the
  // client/project selectors and the cart/checkout column. Cart & checkout only
  // work inside a project workflow (where urlProjectId is present).
  const adminBrowseMode =
    isAdminFallback && !isClient && !urlProjectId && !selectionMode && !fromPlanning;

  // Cart column only inside client/project workflow (/sites?projectId=…), not on standalone /sites browse.
  const showCartColumn =
    canUseProjectCart &&
    !selectionMode &&
    !fromPlanning &&
    Boolean(urlProjectId);

  // Archive / price-change Actions column: standalone /sites catalog only — not in
  // project/client workflow Explore Sites (/sites?projectId=…).
  const showSiteActionsColumn = !fromPlanning && !urlProjectId;
  
  // Column visibility configuration - role-based
  const getDefaultColumns = () => {
    const baseColumns = [
      { id: 'siteUrl', label: 'Site URL', visible: true, required: true },
      { id: 'da', label: 'DA', visible: true },
      { id: 'dr', label: 'DR', visible: true },
      { id: 'traffic', label: 'Traffic', visible: true },
      { id: 'ss', label: 'SS', visible: false },
      { id: 'keywords', label: 'Keywords', visible: false },
      { id: 'category', label: 'Category', visible: true },
      { id: 'country', label: 'Country', visible: true },
      { id: 'language', label: 'Language', visible: false },
      { id: 'tat', label: 'TAT', visible: true },
      { id: 'status', label: 'Status', visible: true },
    ];

    if (isSuperAdminFallback || isAdminFallback) {
      if (canSite('sites.column.outreach_email')) {
        baseColumns.push({ id: 'outreachEmail', label: 'Outreach Email', visible: true });
      }
    }

    if (isSuperAdminFallback || isPublisher || canSite('sites.column.base_prices')) {
      baseColumns.push(
        { id: 'gbBasePrice', label: 'GB Base Price', visible: true },
        { id: 'liBasePrice', label: 'LI Base Price', visible: true }
      );
    }

    // Admin and Client see displayed prices with markup
    if (canUseProjectCart) {
      baseColumns.push(
        { id: 'cart', label: 'Cart', visible: true },
        { id: 'gbPrice', label: 'GB Price', visible: true },
        { id: 'liPrice', label: 'LI Price', visible: true }
      );
    }

    // Admin and Super Admin see publisher column
    if (isAdminFallback || isSuperAdminFallback) {
      baseColumns.push({ id: 'publisher', label: 'Publisher', visible: true });
    }

    // Actions column for Super Admin and Publisher (standalone /sites only)
    if (showSiteActionsColumn && (canSite('sites.edit') || isPublisher)) {
      baseColumns.push({ id: 'actions', label: 'Actions', visible: true, required: true });
    }

    return baseColumns;
  };

  const DEFAULT_COLUMNS = getDefaultColumns();

  const { columns, toggleColumn, resetDefaults, isColumnVisible } = useColumnVisibility(
    `guestBlogSites_columnVisibility_${user?.role || 'guest'}`,
    DEFAULT_COLUMNS
  );
  
  const [state, setState] = useState<GuestBlogSitesPageState>({
    page: 0,
    rowsPerPage: 10,
    search: urlSearchFromQuery,
    category: categoryFromDashboard, // Initialize with category from Dashboard
    country: '',
    status: '',
    publisherId: '',
    outreachEmail: '',
    clientId: urlClientId,
    projectId: urlProjectId,
    // Only filter the site list by plan in the legacy planning plan-view (from=planning).
    // In the project workflow the planId rides in the URL purely to remember the active
    // plan on the Plan page, so Explore Sites must still show ALL sites to add more.
    planId: selectionMode || !fromPlanning ? '' : urlPlanId,
    minDr: '',
    maxDr: '',
    minTraffic: '',
    maxTraffic: '',
    minPrice: '',
    maxPrice: '',
    fromDate: '',
    toDate: '',
    sortBy: 'dr',
    sortOrder: 'desc',
  });

  useEffect(() => {
    if (fromAISuggestions) {
      setState(prev => ({
        ...prev,
        page: 0,
        rowsPerPage: 50,
        search: '',
        category: '',
        country: '',
        status: '',
        publisherId: '',
        clientId: urlClientId,
        projectId: urlProjectId,
        planId: '',
      }));
      setSearchInput('');
      setDrInput({ min: '', max: '' });
      setTrafficInput({ min: '', max: '' });
      setPriceInput({ min: '', max: '' });
    }
  }, [fromAISuggestions, urlClientId, urlProjectId]);

  useEffect(() => {
    if (paymentStatus !== 'success' || !paymentAttemptId || !stripeSessionId || paymentFinalizing) {
      return;
    }

    const finalizePayment = async () => {
      setPaymentFinalizing(true);
      try {
        const response = await cartService.finalizeStripeCheckout({
          paymentAttemptId,
          sessionId: stripeSessionId,
        });

        queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
        if (urlProjectId) {
          await fetchCart();
        }

        setSnackbar({
          open: true,
          message: response.message || 'Payment successful and placements created.',
          severity: 'success',
        });
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || 'Payment completed but finalization failed. Please contact support.',
          severity: 'error',
        });
      } finally {
        const updatedParams = new URLSearchParams(location.search);
        updatedParams.delete('paymentStatus');
        updatedParams.delete('paymentAttemptId');
        updatedParams.delete('session_id');
        updatedParams.delete('paymentCancelled');
        navigate(`${location.pathname}${updatedParams.toString() ? `?${updatedParams.toString()}` : ''}`, { replace: true });
        setPaymentFinalizing(false);
      }
    };

    void finalizePayment();
  }, [
    paymentStatus,
    paymentAttemptId,
    stripeSessionId,
    paymentFinalizing,
    queryClient,
    urlProjectId,
    fetchCart,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!paymentCancelled) return;

    setSnackbar({
      open: true,
      message: 'Payment cancelled. Your cart is unchanged.',
      severity: 'warning',
    });

    const updatedParams = new URLSearchParams(location.search);
    updatedParams.delete('paymentCancelled');
    navigate(`${location.pathname}${updatedParams.toString() ? `?${updatedParams.toString()}` : ''}`, { replace: true });
  }, [paymentCancelled, location.pathname, location.search, navigate]);

  const updateAISuggestionsContext = (nextClientId?: string, nextProjectId?: string) => {
    const params = new URLSearchParams(location.search);
    params.set('from', 'ai-suggestions');
    if (selectedSiteIdsParam) {
      params.set('selectedSiteIds', selectedSiteIdsParam);
    }

    const finalClientId = nextClientId ?? (isClient && user?.clientId ? user.clientId : state.clientId);
    if (finalClientId) {
      params.set('clientId', finalClientId);
    } else {
      params.delete('clientId');
    }

    if (nextProjectId) {
      params.set('projectId', nextProjectId);
    } else {
      params.delete('projectId');
    }

    navigate(`/sites?${params.toString()}`);
  };

  // Apply category filter when navigating from Dashboard
  useEffect(() => {
    if (categoryFromDashboard) {
      setState(prev => ({ ...prev, category: categoryFromDashboard }));
      // Clear navigation state to prevent re-applying filter on refresh
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [categoryFromDashboard]);

  // Selection mode state
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [savingSelection, setSavingSelection] = useState(false);

  // Price change request modal state
  const [priceChangeModalOpen, setPriceChangeModalOpen] = useState(false);
  const [selectedSiteForPriceChange, setSelectedSiteForPriceChange] = useState<GuestBlogSite | null>(null);

  // Expandable filters state
  const [showAllFilters, setShowAllFilters] = useState(false);

  // Separate state for search input to enable debouncing
  const [searchInput, setSearchInput] = useState(urlSearchFromQuery);

  // Apply ?search= / ?domain= when navigating from Requested sites (or changing query while mounted)
  useEffect(() => {
    if (!urlSearchFromQuery) return;
    setSearchInput(urlSearchFromQuery);
    setState((prev) =>
      prev.search === urlSearchFromQuery && prev.page === 0
        ? prev
        : { ...prev, search: urlSearchFromQuery, page: 0 }
    );
  }, [location.search, urlSearchFromQuery]);
  const [drInput, setDrInput] = useState({ min: '', max: '' });
  const [trafficInput, setTrafficInput] = useState({ min: '', max: '' });
  const [priceInput, setPriceInput] = useState({ min: '', max: '' });

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, search: searchInput, page: 0 }));
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounced DR filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, minDr: drInput.min, maxDr: drInput.max, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [drInput]);

  // Debounced Traffic filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, minTraffic: trafficInput.min, maxTraffic: trafficInput.max, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [trafficInput]);

  // Debounced Price filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, minPrice: priceInput.min, maxPrice: priceInput.max, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [priceInput]);

  const [formOpen, setFormOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<GuestBlogSite | undefined>();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [checkProgress, setCheckProgress] = useState({ checked: 0, total: 0, errors: 0 });

  // Fetch categories dynamically
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-dropdown'],
    queryFn: categoryService.getCategoriesForDropdown,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  
  const categories = categoriesData || [];
  
  // Debug logging
  // console.log('GuestBlogSitesPage - User:', user);
  // console.log('GuestBlogSitesPage - isAdmin:', isAdmin);
  // console.log('GuestBlogSitesPage - isSuperAdmin:', isSuperAdmin);
  // console.log('GuestBlogSitesPage - isSuperAdminFallback:', isSuperAdminFallback);
  // console.log('GuestBlogSitesPage - isAdminFallback:', isAdminFallback);
  // console.log('GuestBlogSitesPage - isPublisher:', isPublisher);

  // Sync state with URL parameters when they change
  // For CLIENT role, automatically set their clientId
  useEffect(() => {
    setState(prev => ({
      ...prev,
      clientId: isClient && user?.clientId ? user.clientId : urlClientId,
      projectId: urlProjectId,
      page: 0 // Reset to first page when context changes
    }));
  }, [urlClientId, urlProjectId, isClient, user?.clientId]);

  // Fetch cart when project context is available
  // Only fetch once when component mounts with valid project and authenticated user
  // Publishers don't have cart access, but CLIENT users do
  useEffect(() => {
    if (urlProjectId && user && user.id && !isPublisher) {
      fetchCart();
    }
    // Intentionally not including fetchCart in dependencies to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProjectId, user?.id, isPublisher]);

  // Fetch all cart items when no project context
  // Publishers don't have cart access, but CLIENT users do
  useEffect(() => {
    const fetchAllCartItems = async () => {
      if (!urlProjectId && user && user.id && !isPublisher) {
        setAllCartLoading(true);
        try {
          const response = await cartService.getAllCartItems();
          setAllCartData(response.data);
        } catch (error) {
          console.error('Error fetching all cart items:', error);
          setAllCartData(null);
        } finally {
          setAllCartLoading(false);
        }
      }
    };

    fetchAllCartItems();
  }, [urlProjectId, user?.id, isPublisher]);

  // Cart popup state (for adding items)
  const [cartPopupOpen, setCartPopupOpen] = useState(false);
  const [selectedSiteForCart, setSelectedSiteForCart] = useState<GuestBlogSite | null>(null);
  const [rowCartDocPreview, setRowCartDocPreview] = useState<{ url: string; name: string } | null>(null);
  const [rowCartUploadingSiteId, setRowCartUploadingSiteId] = useState<string | null>(null);
  const [removingCartSiteId, setRemovingCartSiteId] = useState<string | null>(null);
  const [contentUrlDialogOpen, setContentUrlDialogOpen] = useState(false);
  const [contentUrlDialogSite, setContentUrlDialogSite] = useState<GuestBlogSite | null>(null);
  const [contentUrlInput, setContentUrlInput] = useState('');

  // Fetch publisher's existing price change requests
  const { data: priceChangeRequestsData } = useQuery({
    queryKey: ['price-change-requests'],
    queryFn: () => priceChangeRequestService.getRequests(),
    enabled: isPublisher,
    staleTime: 30 * 1000,
  });

  // Map site IDs to their latest price change request
  const priceChangeRequestsBySite: Record<string, PriceChangeRequest> = {};
  if (priceChangeRequestsData) {
    priceChangeRequestsData.forEach((req: PriceChangeRequest) => {
      // Keep the most recent request per site
      if (!priceChangeRequestsBySite[req.siteId] || 
          new Date(req.requestedAt) > new Date(priceChangeRequestsBySite[req.siteId].requestedAt)) {
        priceChangeRequestsBySite[req.siteId] = req;
      }
    });
  }

  // Fetch ordered sites using React Query for proper cache management
  const { data: orderedSitesData } = useQuery({
    queryKey: ['ordered-sites', state.clientId],
    queryFn: () => guestBlogOrderService.getOrderedSites(state.clientId),
    enabled: !!state.clientId && !isClient, // CLIENT users don't need ordered sites data
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: false, // Don't refetch on every mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  const orderedSites = orderedSitesData?.data || [];

  // Fetch plans for client (for plan filter dropdown - for all users with clientId)
  const { data: plansData } = useQuery({
    queryKey: ['client-plans-for-filter', state.clientId],
    queryFn: () => clientPlanService.getPlans({ 
      clientId: state.clientId || undefined,
      status: 'ACTIVE',
      // The plan filter dropdown belongs to the Planning module — workflow
      // saved orders (ORDER_DRAFT) are excluded.
      planType: 'PLANNING'
    }),
    enabled: !!state.clientId && !selectionMode, // Fetch plans when clientId is available
  });

  const clientPlans = plansData?.data || [];

  // Fetch existing plan sites when in selection mode to pre-select them
  const { data: existingPlanSitesData } = useQuery({
    queryKey: ['plan-sites', selectionPlanId],
    queryFn: () => clientPlanService.getPlanSites(selectionPlanId),
    enabled: selectionMode && !!selectionPlanId,
  });

  // Fetch guest blog sites
  const {
    data: sitesData,
    isLoading,
  } = useQuery({
    queryKey: ['guestBlogSites', state.page, state.rowsPerPage, state.search, state.category, state.country, state.status, state.publisherId, state.outreachEmail, state.clientId, state.projectId, state.planId, state.minDr, state.maxDr, state.minTraffic, state.maxTraffic, state.minPrice, state.maxPrice, state.fromDate, state.toDate, state.sortBy, state.sortOrder],
    queryFn: () => guestBlogSiteService.getGuestBlogSites({
      page: fromAISuggestions ? 1 : state.page + 1,
      limit: fromAISuggestions ? 500 : state.rowsPerPage,
      search: state.search || undefined,
      category: state.category || undefined,
      country: state.country || undefined,
      status: state.status || undefined,
      publisherId: state.publisherId || undefined,
      outreachEmail: state.outreachEmail || undefined,
      clientId: state.clientId || undefined,
      projectId: state.projectId || undefined,
      planId: state.planId || undefined,
      includeUsageStats: state.projectId ? 'true' : undefined,
      minDr: state.minDr && state.minDr.trim() !== '' ? parseInt(state.minDr) : undefined,
      maxDr: state.maxDr && state.maxDr.trim() !== '' ? parseInt(state.maxDr) : undefined,
      minTraffic: state.minTraffic && state.minTraffic.trim() !== '' ? parseInt(state.minTraffic) : undefined,
      maxTraffic: state.maxTraffic && state.maxTraffic.trim() !== '' ? parseInt(state.maxTraffic) : undefined,
      minPrice: state.minPrice && state.minPrice.trim() !== '' ? parseFloat(state.minPrice) : undefined,
      maxPrice: state.maxPrice && state.maxPrice.trim() !== '' ? parseFloat(state.maxPrice) : undefined,
      fromDate: localDayBoundaryIso(state.fromDate, 'start'),
      toDate: localDayBoundaryIso(state.toDate, 'end'),
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
    }),
    placeholderData: (previousData) => previousData,
    // Keep results warm so returning to Explore Sites between workflow steps
    // doesn't blank-flash + refetch every time.
    staleTime: 60 * 1000,
  });

  // Fetch publishers for filter dropdown (only when publisher filter is permitted)
  const { data: publishersData } = useQuery({
    queryKey: ['publishers', 'site-workflow'],
    queryFn: () => publisherService.getPublishersForSiteWorkflow(),
    enabled: adminCanFilterPublisher,
  });

  // Fetch clients for dropdown (only for Admin/Super Admin)
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
    enabled: !isPublisher && !isClient, // Don't fetch for Publishers or CLIENT users
  });

  // Fetch projects for selected client (or for CLIENT role user)
  const { data: projectsData } = useQuery({
    queryKey: ['projects', state.clientId, isClient ? user?.clientId : null],
    queryFn: () => {
      const clientId = isClient && user?.clientId ? user.clientId : state.clientId;
      return clientId ? projectService.getProjects({ clientId, limit: 1000 }) : Promise.resolve({ data: [], pagination: { page: 0, limit: 0, total: 0, pages: 0 } });
    },
    enabled: (isClient && !!user?.clientId) || (!isClient && !!state.clientId),
  });

  const rawSites = sitesData?.data || [];
  const aiFilteredSites = fromAISuggestions
    ? rawSites.filter((site: GuestBlogSite) => selectedSiteIdsSet.has(site.id))
    : rawSites;
  const totalCount = fromAISuggestions
    ? aiFilteredSites.length
    : sitesData?.pagination?.total || 0;
  const sites = fromAISuggestions
    ? aiFilteredSites.slice(
        state.page * state.rowsPerPage,
        state.page * state.rowsPerPage + state.rowsPerPage
      )
    : rawSites;
  const publishers = (publishersData ?? []).sort((a, b) =>
    (a.publisherName || '').localeCompare(b.publisherName || '', undefined, { sensitivity: 'base' })
  );
  const clients = (clientsData?.data || []).sort((a, b) => 
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );
  const projects = projectsData?.data || [];
  const outreachEmailOptions = Array.from(
    new Set(
      sites
        .map((site: GuestBlogSite) => (site.outreach_email || '').trim())
        .filter((email: string) => email.length > 0)
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  // Pre-select existing plan sites when entering selection mode
  useEffect(() => {
    if (selectionMode && existingPlanSitesData?.data) {
      const existingSiteIds = existingPlanSitesData.data.map((site: any) => site.guestBlogSiteId);
      setSelectedSites(new Set(existingSiteIds));
    }
  }, [selectionMode, existingPlanSitesData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: guestBlogSiteService.createGuestBlogSite,
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
      clearGuestBlogSiteAddDraft();
      setFormOpen(false);
      setSnackbar({
        open: true,
        message: response?.message || 'Guest blog site created successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to create guest blog site',
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => guestBlogSiteService.updateGuestBlogSite(id, data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
      setFormOpen(false);
      setSelectedSite(undefined);
      setSnackbar({
        open: true,
        message: response?.message || 'Guest blog site updated successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update guest blog site',
        severity: 'error',
      });
    },
  });


  const deleteMutation = useMutation({
    mutationFn: guestBlogSiteService.deleteGuestBlogSite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
      setSnackbar({
        open: true,
        message: 'Guest blog site archived successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to archive guest blog site',
        severity: 'error',
      });
    },
  });

  const removeSiteFromPlanMutation = useMutation({
    mutationFn: ({ planId, siteId }: { planId: string; siteId: string }) =>
      clientPlanService.removeSiteFromPlan(planId, siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
      setSnackbar({
        open: true,
        message: 'Site removed from plan successfully!',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to remove site from plan',
        severity: 'error',
      });
    },
  });

  // Selection mode handlers
  const handleToggleSite = (siteId: string) => {
    setSelectedSites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const currentPageSiteIds = sites.map((site: any) => site.id);
    setSelectedSites(prev => {
      const newSet = new Set(prev);
      currentPageSiteIds.forEach(id => newSet.add(id));
      return newSet;
    });
  };

  const handleDeselectAll = () => {
    setSelectedSites(new Set());
  };

  // Export filtered data to CSV
  const handleExportCSV = async () => {
    if (totalCount === 0) {
      setSnackbar({
        open: true,
        message: 'No data to export',
        severity: 'info',
      });
      return;
    }

    try {
      // Show loading message
      setSnackbar({
        open: true,
        message: 'Preparing export...',
        severity: 'info',
      });

      // Fetch all data with same filters but no pagination limit
      const allDataResponse = await guestBlogSiteService.getGuestBlogSites({
        page: 1,
        limit: 10000, // Large limit to get all data
        search: state.search,
        category: state.category,
        country: state.country,
        status: state.status || undefined,
        publisherId: state.publisherId,
        outreachEmail: state.outreachEmail,
        clientId: state.clientId,
        projectId: state.projectId,
        planId: state.planId,
        minDr: state.minDr ? parseInt(state.minDr) : undefined,
        maxDr: state.maxDr ? parseInt(state.maxDr) : undefined,
        minTraffic: state.minTraffic ? parseInt(state.minTraffic) : undefined,
        maxTraffic: state.maxTraffic ? parseInt(state.maxTraffic) : undefined,
        minPrice: state.minPrice ? parseFloat(state.minPrice) : undefined,
        maxPrice: state.maxPrice ? parseFloat(state.maxPrice) : undefined,
        fromDate: localDayBoundaryIso(state.fromDate, 'start'),
        toDate: localDayBoundaryIso(state.toDate, 'end'),
      });

      const allSites = allDataResponse?.data || [];

      if (allSites.length === 0) {
        setSnackbar({
          open: true,
          message: 'No data to export',
          severity: 'info',
        });
        return;
      }

      // CSV Headers
      const headers = [
        'Site URL',
        'Publisher',
        'Outreach Email',
        'DA',
        'DR',
        'Traffic',
        'SS',
        'Keywords',
        'Category',
        'Country',
        'Language',
        'TAT',
        'GB Price',
        'LI Price',
        'GB Client Price',
        'LI Client Price',
        'Status',
        'Negotiation Status',
      ];

      // CSV Rows
      const rows = allSites.map((site: any) => [
        site.site_url,
        site.publisher?.publisherName || 'N/A',
        site.outreach_email || 'N/A',
        site.da,
        site.dr,
        site.ahrefs_traffic,
        site.ss || 'N/A',
        site.keywords || 'N/A',
        site.category,
        site.country,
        site.site_language,
        site.tat,
        Math.round(site.base_price),
        site.li_base_price ? Math.round(site.li_base_price) : 'N/A',
        Math.round(site.displayed_price || site.base_price),
        site.li_displayed_price ? Math.round(site.li_displayed_price) : (site.li_base_price ? Math.round(site.li_base_price) : 'N/A'),
        site.status,
        site.negotiation_status || 'N/A',
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => 
          row.map(cell => {
            // Escape cells containing commas or quotes
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(',')
        ),
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `guest_blog_sites_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: `Exported ${allSites.length} sites to CSV`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export data',
        severity: 'error',
      });
    }
  };

  const handleSaveSelection = async () => {
    if (selectedSites.size === 0) {
      setSnackbar({ open: true, message: 'Please select at least one site', severity: 'error' });
      return;
    }

    setSavingSelection(true);
    try {
      // Get existing sites in the plan
      const existingSiteIds = existingPlanSitesData?.data?.map((site: any) => site.guestBlogSiteId) || [];
      const selectedSiteIds = Array.from(selectedSites);

      // Find sites to add (selected but not in existing)
      const sitesToAdd = selectedSiteIds.filter(id => !existingSiteIds.includes(id));
      
      // Find sites to remove (in existing but not selected)
      const sitesToRemove = existingSiteIds.filter((id: string) => !selectedSites.has(id));

      // Add new sites
      if (sitesToAdd.length > 0) {
        await clientPlanService.addSitesToPlan(selectionPlanId, sitesToAdd);
      }

      // Remove unchecked sites
      if (sitesToRemove.length > 0) {
        for (const siteId of sitesToRemove) {
          await clientPlanService.removeSiteFromPlan(selectionPlanId, siteId);
        }
      }

      const addedCount = sitesToAdd.length;
      const removedCount = sitesToRemove.length;
      let message = 'Plan updated successfully';
      if (addedCount > 0 && removedCount > 0) {
        message = `${addedCount} site(s) added, ${removedCount} site(s) removed`;
      } else if (addedCount > 0) {
        message = `${addedCount} site(s) added to plan`;
      } else if (removedCount > 0) {
        message = `${removedCount} site(s) removed from plan`;
      }

      setSnackbar({ 
        open: true, 
        message: message, 
        severity: 'success' 
      });
      setTimeout(() => navigate('/planning'), 1500);
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Failed to save sites', 
        severity: 'error' 
      });
    } finally {
      setSavingSelection(false);
    }
  };

  const handleCancelSelection = () => {
    navigate('/planning');
  };

  const handleAddNew = () => {
    setSelectedSite(undefined);
    setFormOpen(true);
  };

  const handleCheckAllSites = async () => {
    try {
      setIsCheckingStatus(true);
      setCheckProgress({ checked: 0, total: 0, errors: 0 });
      
      // Start the check
      const response = await guestBlogSiteService.checkAllSitesStatus();
      const totalSites = response.totalSites || 0;
      
      setSnackbar({
        open: true,
        message: `Started checking ${totalSites} sites...`,
        severity: 'info',
      });
      
      setCheckProgress({ checked: 0, total: totalSites, errors: 0 });
      
      // Poll every 3 seconds to check progress
      let pollCount = 0;
      const maxPolls = 40; // 2 minutes max
      
      const pollInterval = setInterval(async () => {
        pollCount++;
        
        try {
          // Use the service method with cache-busting
          const freshData = await guestBlogSiteService.getGuestBlogSites({
            page: 1,
            limit: 1000,
            _: Date.now()
          });
          
          const allSites = freshData?.data || [];
          const checkedSites = allSites.filter((s) => s.siteLastChecked);
          const errorSites = allSites.filter((s) => s.siteLastChecked && !isGuestBlogSiteHealthOk(s));
          
          setCheckProgress({
            checked: checkedSites.length,
            total: totalSites,
            errors: errorSites.length
          });
          
          // Also refresh the main query to update status indicators in table
          queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
          
          // If all sites checked or max polls reached
          if (checkedSites.length >= totalSites || pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setIsCheckingStatus(false);
            
            setSnackbar({
              open: true,
              message: `Completed! ${errorSites.length} errors out of ${totalSites} sites`,
              severity: errorSites.length > 0 ? 'warning' : 'success',
            });
            
            // Final refresh to ensure all data is updated
            queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
          }
        } catch (err) {
          console.error('Error polling status:', err);
          // If we get an error, stop polling after 3 attempts
          if (pollCount >= 3) {
            clearInterval(pollInterval);
            setIsCheckingStatus(false);
            setSnackbar({
              open: true,
              message: 'Error checking site status. Please try again.',
              severity: 'error',
            });
          }
        }
      }, 3000);
      
    } catch (error: any) {
      setIsCheckingStatus(false);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to check site statuses',
        severity: 'error',
      });
    }
  };

  const handleEdit = (site: GuestBlogSite) => {
    setSelectedSite(site);
    setFormOpen(true);
  };

  const handleDelete = (site: GuestBlogSite) => {
    if (window.confirm(`Are you sure you want to delete ${site.site_url}?`)) {
      deleteMutation.mutate(site.id);
    }
  };

  const handleSubmit = async (data: any) => {
    if (selectedSite) {
      updateMutation.mutate({ id: selectedSite.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedSite(undefined);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleCategoryChange = (event: any) => {
    setState(prev => ({ ...prev, category: event.target.value, page: 0 }));
  };

  const handleCountryChange = (event: any) => {
    setState(prev => ({ ...prev, country: event.target.value, page: 0 }));
  };

  const handleStatusChange = (event: any) => {
    setState(prev => ({ ...prev, status: event.target.value, page: 0 }));
  };

  const handlePublisherChange = (event: any) => {
    setState(prev => ({ ...prev, publisherId: event.target.value, page: 0 }));
  };

  const handleOutreachEmailChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setState(prev => ({ ...prev, outreachEmail: event.target.value, page: 0 }));
  };

  const handleClientChange = (event: SelectChangeEvent) => {
    const newClientId = event.target.value;
    
    // Reset project if client changes
    setState(prev => ({ ...prev, clientId: newClientId, projectId: '', page: 0 }));
    
    // React Query will automatically refetch ordered sites when clientId changes
  };

  // Handler for native HTML select element
  const handleClientChangeNative = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newClientId = event.target.value;
    
    // Reset project if client changes
    setState(prev => ({ ...prev, clientId: newClientId, projectId: '', page: 0 }));
    
    // React Query will automatically refetch ordered sites when clientId changes
  };

  const handleProjectChange = (event: SelectChangeEvent) => {
    const newProjectId = event.target.value;
    
    if (newProjectId) {
      // Update URL with new projectId and keep clientId
      navigate(`/sites?projectId=${newProjectId}&clientId=${state.clientId}`);
    } else {
      // If no project selected, just show client context
      navigate(`/sites?clientId=${state.clientId}`);
    }
  };

  // Handler for native HTML select element (admin/super-admin browse on /sites).
  // Keep the selection in LOCAL STATE only — do NOT push projectId into the URL.
  // A projectId in the URL switches /sites into the project workflow (cart /
  // orders); here the admin only wants client pricing + project filtering, so we
  // stay in browse mode (cart/checkout remain hidden via adminBrowseMode).
  const handleProjectChangeNative = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newProjectId = event.target.value;
    setState(prev => ({ ...prev, projectId: newProjectId, page: 0 }));
  };

  const handleDrChange = (field: 'min' | 'max') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDrInput(prev => ({ ...prev, [field]: value }));
  };

  const handleTrafficChange = (field: 'min' | 'max') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setTrafficInput(prev => ({ ...prev, [field]: value }));
  };

  const handlePriceChange = (field: 'min' | 'max') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPriceInput(prev => ({ ...prev, [field]: value }));
  };

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, fromDate: event.target.value, page: 0 }));
  };

  const handleToDateChange = (event: any) => {
    setState(prev => ({ ...prev, toDate: event.target.value, page: 0 }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setState(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setState(prev => ({ ...prev, rowsPerPage: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleSort = (field: string) => {
    setState(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 0
    }));
  };

  // Pagination helper function
  const getPaginationPages = () => {
    const totalPages = Math.ceil(totalCount / state.rowsPerPage);
    const currentPage = state.page;
    const pages: (number | string)[] = [];
    
    // Always show first page
    pages.push(0);
    
    // Calculate range around current page
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages - 2, currentPage + 2);
    
    // Add ellipsis after first page if needed
    if (startPage > 1) {
      pages.push('...');
    }
    
    // Add pages in range
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 2) {
      pages.push('...');
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      pages.push(totalPages - 1);
    }
    
    return pages;
  };

  // Cart functions
  const handleAddToCart = (site: GuestBlogSite) => {
    // For CLIENT users, auto-use their client context
    let clientId = state.clientId;
    let projectId = state.projectId;
    
    if (isClient && user?.clientId) {
      clientId = user.clientId;
      // For CLIENT users, if no project selected, we'll handle this in the cart form
    }
    
    if (!clientId && !isClient) {
      setSnackbar({
        open: true,
        message: 'Please select a client before adding to cart.',
        severity: 'error',
      });
      return;
    }
    
    if (!projectId && !isClient) {
      setSnackbar({
        open: true,
        message: 'Please select a project for the selected client before adding to cart.',
        severity: 'error',
      });
      return;
    }

    // Check if site is already in cart for this project
    if (cartData?.cartItems?.some(item => item.siteId === site.id)) {
      setSnackbar({
        open: true,
        message: 'This site is already in your cart for this project.',
        severity: 'error',
      });
      return;
    }

    // Note: We no longer block adding sites that were previously ordered
    // The "Already Ordered" badge serves as a warning, but users can still add the site
    // This supports the planning module workflow where sites can be reused

    setSelectedSiteForCart(site);
    setCartPopupOpen(true);
  };

  const addItemToCart = async (data: { contentText: string; contentDocUrl?: string; contentUrl?: string; orderDate?: string; submissionType: 'GBS' | 'LI' | 'BOTH'; price: number }) => {
    if (!selectedSiteForCart) {
      setSnackbar({
        open: true,
        message: 'No site selected for cart',
        severity: 'error',
      });
      return;
    }

    // Backend requires projectId + clientId + siteId.
    // Always derive from current UI context (state), not just the URL.
    let clientId = state.clientId;
    let projectId = state.projectId;

    if (isClient && user?.clientId) clientId = user.clientId;

    if (!clientId) {
      setSnackbar({
        open: true,
        message: 'No client context available',
        severity: 'error',
      });
      return;
    }

    if (!projectId) {
      setSnackbar({
        open: true,
        message: 'Please select a project before adding to cart.',
        severity: 'error',
      });
      return;
    }

    try {
      // Pass content data to addToCart
      await addToCart(clientId, selectedSiteForCart.id, {
        contentText: data.contentText,
        contentDocUrl: data.contentDocUrl,
        contentUrl: data.contentUrl,
        orderDate: data.orderDate,
        submissionType: data.submissionType,
        price: data.price
      });
      bumpUsageCountInCache(queryClient, selectedSiteForCart.id, 1);
      void queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });

      setCartPopupOpen(false);
      setSelectedSiteForCart(null);
      setSnackbar({
        open: true,
        message: `Site added to cart successfully!`,
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add item to cart',
        severity: 'error',
      });
    }
  };

  const getCartItemForSite = (siteId: string) =>
    cartData?.cartItems?.find((item) => item.siteId === siteId);

  const handleCartRowSelectToggle = async (site: GuestBlogSite) => {
    const existing = getCartItemForSite(site.id);
    if (existing) {
      setRemovingCartSiteId(site.id);
      try {
        await removeFromCart(existing.id);
        bumpUsageCountInCache(queryClient, site.id, -1);
        void queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
        // Keep the saved plan in sync with the cart in the background so the
        // row toggles instantly instead of waiting on the extra round-trip.
        if (urlPlanId) {
          void clientPlanService
            .removeSiteFromPlan(urlPlanId, site.id)
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['plan-sites', urlPlanId] });
              queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
            })
            .catch(() => undefined);
        }
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || error.message || 'Failed to remove from cart',
          severity: 'error',
        });
      } finally {
        setRemovingCartSiteId(null);
      }
      return;
    }
    handleAddToCart(site);
  };

  // Workflow "Explore Sites" quick add: add straight to cart with no content/doc URL.
  // The Google Doc link is collected later in the Place Order step.
  const handleQuickAddToCart = async (site: GuestBlogSite) => {
    let clientId = state.clientId;
    if (isClient && user?.clientId) clientId = user.clientId;
    if (!clientId) {
      setSnackbar({ open: true, message: 'Please select a client first.', severity: 'error' });
      return;
    }
    if (!state.projectId) {
      setSnackbar({ open: true, message: 'Please select a project first.', severity: 'error' });
      return;
    }
    // Already in this plan → it's already shown as "Added"; nothing to do.
    if (cartData?.cartItems?.some(item => item.siteId === site.id)) {
      return;
    }
    setRowCartUploadingSiteId(site.id);
    try {
      const price =
        typeof site.displayed_price === 'number' ? site.displayed_price : Number(site.base_price) || 0;
      await addToCart(clientId, site.id, { submissionType: 'GBS', price });
      bumpUsageCountInCache(queryClient, site.id, 1);
      void queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
      // When editing an existing saved order, persist to the plan so additions
      // survive refresh. Run it in the background — the cart already updated the
      // row's "Added" state, so we don't make the user wait on this round-trip.
      if (urlPlanId) {
        void clientPlanService
          .addSitesToPlan(urlPlanId, [site.id])
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['plan-sites', urlPlanId] });
            queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
          })
          .catch(() => undefined);
      }
      setSnackbar({ open: true, message: 'Site added to plan', severity: 'success' });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to add site',
        severity: 'error',
      });
    } finally {
      setRowCartUploadingSiteId(null);
    }
  };

  const handleRowCartContentUrlSet = (site: GuestBlogSite) => {
    const existing = getCartItemForSite(site.id);
    setContentUrlDialogSite(site);
    setContentUrlInput(existing?.contentUrl || '');
    setContentUrlDialogOpen(true);
  };

  const handleRowCartContentUrlSave = async () => {
    if (!urlProjectId || !contentUrlDialogSite) return;

    const site = contentUrlDialogSite;
    const existing = getCartItemForSite(site.id);
    const contentUrl = contentUrlInput.trim();
    if (!contentUrl) {
      setSnackbar({ open: true, message: 'Please provide a valid Google Doc URL.', severity: 'error' });
      return;
    }
    try {
      const parsed = new URL(contentUrl);
      if (!/^https?:$/i.test(parsed.protocol)) {
        throw new Error('invalid protocol');
      }
    } catch {
      setSnackbar({ open: true, message: 'Invalid URL. Please paste a full http/https link.', severity: 'error' });
      return;
    }

    let clientId = state.clientId;
    if (isClient && user?.clientId) clientId = user.clientId;
    if (!clientId) {
      setSnackbar({ open: true, message: 'Please select a client before adding URL.', severity: 'error' });
      return;
    }
    if (!state.projectId) {
      setSnackbar({ open: true, message: 'Please select a project before adding URL.', severity: 'error' });
      return;
    }

    setRowCartUploadingSiteId(site.id);
    try {
      const price =
        typeof site.displayed_price === 'number' ? site.displayed_price : Number(site.base_price) || 0;
      if (existing) {
        await cartService.updateCartItem(existing.id, { contentUrl, contentDocUrl: null });
        await fetchCart();
        setSnackbar({ open: true, message: 'Cart content URL updated', severity: 'success' });
      } else {
        await addToCart(clientId, site.id, {
          contentUrl,
          submissionType: 'GBS',
          price,
        });
        bumpUsageCountInCache(queryClient, site.id, 1);
        void queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
        setSnackbar({ open: true, message: 'Added to cart with content URL', severity: 'success' });
      }
      setContentUrlDialogOpen(false);
      setContentUrlDialogSite(null);
      setContentUrlInput('');
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to set content URL',
        severity: 'error',
      });
    } finally {
      setRowCartUploadingSiteId(null);
    }
  };


  // Cart functions are now handled by CartContext (useCart hook)
  // - cartTotal: total price of cart items
  // - cartCount: number of items in cart  
  // - addToCart: add item to cart
  // - removeFromCart: remove item from cart
  // - checkout: process cart and create placements

  // Cart is now persisted via backend API through CartContext
  // No need for localStorage persistence

  // Auto-select first client for admin users (not super admin)
  React.useEffect(() => {
    if (isAdmin && !isSuperAdmin && clients.length > 0 && !state.clientId) {
      setState(prev => ({
        ...prev,
        clientId: clients[0].id
      }));
    }
  }, [isAdmin, isSuperAdmin, clients, state.clientId]);

  return (
    <>
      <style>{styles}</style>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Back to Planning button */}
            {fromPlanning && (
              <Button
                variant="outlined"
                onClick={() => navigate('/planning')}
                sx={{
                  minWidth: 'auto',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderColor: 'var(--border)',
                  color: 'var(--text-secondary)',
                  '&:hover': {
                    borderColor: 'var(--accent)',
                    backgroundColor: 'var(--accent-soft)',
                    color: 'var(--accent)'
                  }
                }}
              >
                ← Back to Planning
              </Button>
            )}
            <h1 className="page-title">Guest Blog Posting Sites</h1>
          </div>
          
          {/* Cart Icon - hidden in the project workflow (checkout handled by the Cart & Checkout step) */}
          {/* Publishers don't have cart access */}
          {SHOW_PROJECT_CONTEXT_BANNER && !isPublisher && urlProjectId && cartData ? (
            <button
              className="cart-icon-btn"
              onClick={() => setCartSidebarOpen(true)}
              title="View Cart"
            >
              <CartIcon />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
          ) : null}
        </div>

      {/* Explore Sites step summary: how many sites are added + jump to the plan */}
      {inWorkflow && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: '2px 0 18px',
            fontSize: '16px',
          }}
        >
          <CartIcon sx={{ fontSize: 22, color: 'var(--accent)' }} />
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {cartCount} site{cartCount !== 1 ? 's' : ''} added
          </span>
          <span style={{ color: 'var(--text-muted)' }}>—</span>
          <button
            type="button"
            onClick={() => workflow?.goToStep('plan')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--accent)',
              fontFamily: 'inherit',
            }}
          >
            Go to order →
          </button>
        </div>
      )}

      {/* Project Context Banner - hidden in the project workflow (context shown by the workflow shell). */}
      {/* Publishers don't have cart/project context */}
      {SHOW_PROJECT_CONTEXT_BANNER && !isPublisher && urlProjectId && cartData && (
        <ProjectContextBanner
          projectName={cartData.projectName || 'Project'}
          clientName={cartData.clientName || 'Client'}
          clientBudget={cartData.clientBudget || 0}
          projectBudget={cartData.projectBudget || 0}
          cartTotal={cartTotal}
          cartCount={cartCount}
          monthlySpent={(cartData as any).monthlySpent}
          monthPlacementSpent={(cartData as any).monthPlacementSpent}
          monthOrderSpent={(cartData as any).monthOrderSpent}
          onClearContext={() => navigate('/sites')}
          onCartClick={() => setCartSidebarOpen(true)}
          isClient={isClient}
        />
      )}

      {/* Selection Mode Banner */}
      {selectionMode && (
        <Alert 
          severity="info" 
          sx={{ mb: 3, py: 1 }}
        >
          <Typography variant="body2" sx={{ display: 'inline' }}>
            <strong>Selection Mode:</strong> Check/uncheck sites to add or remove from your plan. 
            Sites with <Chip label="In Plan" size="small" color="success" sx={{ fontSize: '0.65rem', height: '18px', mx: 0.5 }} /> are already in this plan. 
            Currently selected: <strong>{selectedSites.size} sites</strong>
            {existingPlanSitesData?.data && ` (${existingPlanSitesData.data.length} already in plan)`}
          </Typography>
        </Alert>
      )}

      {/* AI Suggestions Mode Banner */}
      {fromAISuggestions && (
        <Alert severity="info" sx={{ mb: 3, py: 1 }}>
          <Typography variant="body2" sx={{ display: 'inline' }}>
            <strong>AI Mode:</strong> Showing only the guest blogging sites selected from Category Analysis.
          </Typography>
        </Alert>
      )}

      {fromAISuggestions && (
        <Paper sx={{ p: 2, mb: 2, border: '1px solid #E2E8F0', borderRadius: '10px' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {!isClient && (
              <select
                className="filter-select"
                style={{ minWidth: '220px' }}
                value={state.clientId}
                onChange={(e) => {
                  const nextClientId = e.target.value;
                  updateAISuggestionsContext(nextClientId, '');
                }}
              >
                <option value="">Select Client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            )}

            <select
              className="filter-select"
              style={{ minWidth: '240px' }}
              value={urlProjectId}
              onChange={(e) => {
                const nextProjectId = e.target.value;
                updateAISuggestionsContext(state.clientId, nextProjectId);
              }}
              disabled={!state.clientId}
            >
              <option value="">Select Project</option>
              {projects.map((project: any) => (
                <option key={project.id} value={project.id}>
                  {project.projectName}
                </option>
              ))}
            </select>

            {!urlProjectId && (
              <Typography variant="body2" color="text.secondary">
                Select a project to enable Add to Cart.
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Filters */}
      {!fromAISuggestions && (
      <div className="sites-filter-bar">
        {/* Row 1: Main Filters */}
        <div className="sites-filter-row">
          {/* Search */}
          <div className="filter-search" style={{ minWidth: '210px' }}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search sites by URL or publisher…"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>

          <div className="filter-divider"></div>

          {/* DR Range */}
          <div className="filter-group">
            <span className="filter-label">DR</span>
            <input
              type="number"
              className="filter-input"
              placeholder="Min"
              style={{ width: '64px' }}
              value={drInput.min}
              onChange={handleDrChange('min')}
              min={0}
              max={100}
            />
            <input
              type="number"
              className="filter-input"
              placeholder="Max"
              style={{ width: '64px' }}
              value={drInput.max}
              onChange={handleDrChange('max')}
              min={0}
              max={100}
            />
          </div>

          <div className="filter-divider"></div>

          {/* Traffic Range */}
          <div className="filter-group">
            <span className="filter-label">Traffic</span>
            <input
              type="number"
              className="filter-input"
              placeholder="Min"
              style={{ width: '72px' }}
              value={trafficInput.min}
              onChange={handleTrafficChange('min')}
              min={0}
            />
            <input
              type="number"
              className="filter-input"
              placeholder="Max"
              style={{ width: '72px' }}
              value={trafficInput.max}
              onChange={handleTrafficChange('max')}
              min={0}
            />
          </div>

          <div className="filter-divider"></div>

          {/* Price Range */}
          <div className="filter-group">
            <span className="filter-label">Price</span>
            <input
              type="number"
              className="filter-input"
              placeholder="Min $"
              style={{ width: '68px' }}
              value={priceInput.min}
              onChange={handlePriceChange('min')}
              min={0}
            />
            <input
              type="number"
              className="filter-input"
              placeholder="Max $"
              style={{ width: '68px' }}
              value={priceInput.max}
              onChange={handlePriceChange('max')}
              min={0}
            />
          </div>

          {/* Category */}
          <select
            className="filter-select"
            style={{ minWidth: '130px' }}
            value={state.category}
            onChange={handleCategoryChange}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>

          <div className="filter-spacer"></div>

          {/* More Filters Button */}
          <button
            className={`more-filters-btn ${showAllFilters ? 'open' : ''}`}
            onClick={() => setShowAllFilters(!showAllFilters)}
          >
            More Filters
            <ExpandMoreIcon />
          </button>
        </div>

        {/* More Filters Panel */}
        <div className={`more-filters-panel ${showAllFilters ? 'open' : ''}`}>
          {/* Publisher (Admin and Super Admin only) */}
          {(isAdminFallback || isSuperAdminFallback) && adminCanFilterPublisher && (
            <select
              className="filter-select"
              style={{ minWidth: '140px' }}
              value={state.publisherId}
              onChange={handlePublisherChange}
            >
              <option value="">All Publishers</option>
              {publishers.map((publisher) => (
                <option key={publisher.id} value={publisher.id}>
                  {publisher.publisherName}
                </option>
              ))}
            </select>
          )}
          {(isAdminFallback || isSuperAdminFallback) && adminCanFilterOutreach && (
            <select
              className="filter-select"
              style={{ minWidth: '190px' }}
              value={state.outreachEmail}
              onChange={handleOutreachEmailChange}
            >
              <option value="">All Outreach Emails</option>
              {outreachEmailOptions.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </select>
          )}

          {/* Country */}
          <select
            className="filter-select"
            style={{ minWidth: '120px' }}
            value={state.country}
            onChange={handleCountryChange}
          >
            <option value="">All Countries</option>
            {COUNTRIES.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>

          {/* Status - Hidden for CLIENT users since they only see ACTIVE sites */}
          {!isClient && (isSuperAdminFallback || !isAdminFallback || adminCanFilterStatus) && (
            <>
              <select
                className="filter-select"
                style={{ minWidth: '120px' }}
                value={state.status}
                onChange={handleStatusChange}
              >
                <option value="">All Statuses</option>
                <option value={GuestBlogSiteStatus.ACTIVE}>Active</option>
                <option value={GuestBlogSiteStatus.INACTIVE}>Inactive</option>
              </select>

              <div className="filter-divider"></div>
            </>
          )}

          {/* Date Range */}
          {adminCanFilterDateAdded && (
            <>
          <span className="filter-label">Added</span>
          <input
            type="date"
            className="date-input"
            value={state.fromDate}
            onChange={handleFromDateChange}
            max={new Date().toISOString().split('T')[0]}
          />
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>→</span>
          <input
            type="date"
            className="date-input"
            value={state.toDate}
            onChange={handleToDateChange}
            max={new Date().toISOString().split('T')[0]}
            min={state.fromDate || undefined}
          />
            </>
          )}
        </div>

        {/* Row 2: Client/Project Selection & Action Buttons */}
        {!isPublisher && (
          <div className="filter-row-2">
            {selectionMode ? (
              <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
                Client: <strong>{clients.find(c => c.id === selectionClientId)?.name || 'Unknown'}</strong>
              </div>
            ) : (
              <>
                {/* Client + Project selectors: admin/super admin only on standalone /sites */}
                {!isClient && !isAgency && !inWorkflow && adminCanFilterClientPricing && (
                  <>
                    {/* Client Pricing */}
                    <select
                      className="filter-select"
                      style={{ minWidth: '180px' }}
                      value={state.clientId}
                      onChange={handleClientChangeNative}
                    >
                      <option value="">Client (Pricing)</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {(isAdmin && !isSuperAdmin) || isAgency ? client.name : `${client.name} (${client.percentage ?? 0}%)`}
                        </option>
                      ))}
                    </select>

                    {/* Project */}
                    <select
                      className="filter-select"
                      style={{ minWidth: '160px' }}
                      value={state.projectId}
                      onChange={handleProjectChangeNative}
                      disabled={!state.clientId}
                    >
                      <option value="">Project</option>
                      {projects.map((project: any) => (
                        <option key={project.id} value={project.id}>
                          {project.projectName}
                        </option>
                      ))}
                    </select>

                  </>
                )}
                
                {/* Plan Filter - Show when a project is selected (including the
                    workflow Explore Sites step), but NOT in the standalone
                    /sites browse view (admin just wants pricing). Lists the
                    client's Planning-module plans and is a pure VIEW filter:
                    it narrows the site list to the plan's sites without
                    touching the plan or the current order. */}
                {state.projectId && !adminBrowseMode && (
                  <select
                    className="filter-select"
                    style={{ minWidth: '160px' }}
                    value={state.planId}
                    onChange={(e) => setState(prev => ({ ...prev, planId: e.target.value, page: 0 }))}
                  >
                    <option value="">All Sites</option>
                    {clientPlans.map((plan: any) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planName} ({plan.siteCount || 0} sites)
                      </option>
                    ))}
                  </select>
                )}

              </>
            )}

            <div className="filter-spacer"></div>

            {/* Clear Filters Button */}
            <button
              className="filter-clear-btn"
              onClick={() => {
                setState(prev => ({
                  ...prev,
                  category: '',
                  country: '',
                  status: '',
                  publisherId: '',
                  outreachEmail: '',
                  fromDate: '',
                  toDate: '',
                  page: 0
                }));
                setSearchInput('');
                setDrInput({ min: '', max: '' });
                setTrafficInput({ min: '', max: '' });
                setPriceInput({ min: '', max: '' });
              }}
            >
              <CloseIcon />
              Clear Filters
            </button>

            <div style={{ width: '1px', height: '18px', background: 'var(--border)', flexShrink: 0 }}></div>

            {/* Column Visibility - Available to all roles */}
            {!selectionMode && !fromPlanning && (
              <ColumnVisibilityMenu
                columns={columns}
                onColumnToggle={toggleColumn}
                onResetDefaults={resetDefaults}
              />
            )}

            {/* Action Buttons */}
            {selectionMode ? (
              <>
                <button className="btn btn-secondary btn-sm" onClick={handleCancelSelection}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveSelection}
                  disabled={savingSelection || selectedSites.size === 0}
                >
                  {savingSelection ? 'Saving...' : `Save ${selectedSites.size} Sites`}
                </button>
              </>
            ) : isAdminFallback && !fromPlanning && !inWorkflow ? (
              <>
                {canSite('sites.export') && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleExportCSV}
                    disabled={sites.length === 0}
                  >
                    <DownloadIcon />
                    Export CSV
                  </button>
                )}
                {canSite('sites.bulk_upload') && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setBulkUploadOpen(true)}>
                    <FileUploadIcon />
                    Bulk Upload Sites
                  </button>
                )}
                {canSite('sites.check_all') && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleCheckAllSites}
                    disabled={isCheckingStatus}
                  >
                    {isCheckingStatus ? 'Checking...' : 'Check All Sites'}
                  </button>
                )}
                {canSite('sites.add') && (
                  <button className="btn btn-primary btn-sm" onClick={handleAddNew}>
                    <AddIcon />
                    Add Site
                  </button>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
      )}

      {/* Cart Summary */}
      {/* Cart Summary is now shown in ProjectContextBanner and CartSidebar */}

      {/* Sites Table */}
      <div className="sites-table-container">
        <div className="sites-table-wrapper">
          <table className="sites-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              {/* Checkbox column in selection mode */}
              {selectionMode && (
                <th className="checkbox-cell">
                  <button
                    className="action-btn"
                    onClick={selectedSites.size > 0 ? handleDeselectAll : handleSelectAll}
                    title={selectedSites.size > 0 ? "Deselect All" : "Select All"}
                  >
                    {selectedSites.size > 0 ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                  </button>
                </th>
              )}
              {/* Edit column - moved before Site URL */}
              {(canSite('sites.edit') || isPublisher) && !fromPlanning && <th className="actions-cell">Edit</th>}
              {/* Plan Actions column when viewing a plan - moved to first position */}
              {fromPlanning && !selectionMode && <th className="actions-cell">Plan Actions</th>}
              <th>Site URL</th>
              {isColumnVisible('cart') && showCartColumn && (
                <th style={{ textAlign: 'center', minWidth: '120px' }}>Add Site</th>
              )}
              {/* Usage Stats column when projectId present and not in selection mode */}
              {state.projectId && !selectionMode && <th>Usage</th>}
              {isColumnVisible('publisher') && (isAdminFallback || isSuperAdminFallback) && <th>Publisher</th>}
              {isColumnVisible('outreachEmail') && canSite('sites.column.outreach_email') && <th>Outreach Email</th>}
              {isColumnVisible('da') && (
                <th 
                  className={`sortable ${state.sortBy === 'da' ? 'sort-active' : ''}`}
                  onClick={() => handleSort('da')}
                >
                  DA
                  <span className="sort-icon">
                    <ArrowUpwardIcon className={state.sortBy === 'da' && state.sortOrder === 'asc' ? 'active' : ''} />
                    <ArrowDownwardIcon className={state.sortBy === 'da' && state.sortOrder === 'desc' ? 'active' : ''} />
                  </span>
                </th>
              )}
              {isColumnVisible('dr') && (
                <th 
                  className={`sortable ${state.sortBy === 'dr' ? 'sort-active' : ''}`}
                  onClick={() => handleSort('dr')}
                >
                  DR
                  <span className="sort-icon">
                    <ArrowUpwardIcon className={state.sortBy === 'dr' && state.sortOrder === 'asc' ? 'active' : ''} />
                    <ArrowDownwardIcon className={state.sortBy === 'dr' && state.sortOrder === 'desc' ? 'active' : ''} />
                  </span>
                </th>
              )}
              {isColumnVisible('traffic') && (
                <th 
                  className={`sortable ${state.sortBy === 'ahrefs_traffic' ? 'sort-active' : ''}`}
                  onClick={() => handleSort('ahrefs_traffic')}
                >
                  Traffic
                  <span className="sort-icon">
                    <ArrowUpwardIcon className={state.sortBy === 'ahrefs_traffic' && state.sortOrder === 'asc' ? 'active' : ''} />
                    <ArrowDownwardIcon className={state.sortBy === 'ahrefs_traffic' && state.sortOrder === 'desc' ? 'active' : ''} />
                  </span>
                </th>
              )}
              {isColumnVisible('gbBasePrice') && (isSuperAdminFallback || isPublisher || canSite('sites.column.base_prices')) && <th>GB Base Price</th>}
              {isColumnVisible('liBasePrice') && (isSuperAdminFallback || isPublisher || canSite('sites.column.base_prices')) && <th>LI Base Price</th>}
              {isColumnVisible('gbPrice') && !isPublisher && <th>GB Price</th>}
              {isColumnVisible('liPrice') && !isPublisher && <th>LI Price</th>}
              {isColumnVisible('ss') && <th>SS</th>}
              {isColumnVisible('keywords') && <th>Keywords</th>}
              {isColumnVisible('category') && <th>Category</th>}
              {isColumnVisible('country') && <th>Country</th>}
              {isColumnVisible('language') && <th>Language</th>}
              {isColumnVisible('tat') && <th>TAT</th>}
              {isColumnVisible('status') && <th>Status</th>}
              <th 
                className={`sortable ${state.sortBy === 'createdAt' ? 'sort-active' : ''}`}
                onClick={() => handleSort('createdAt')}
              >
                Site Added Date
                <span className="sort-icon">
                  <ArrowUpwardIcon className={state.sortBy === 'createdAt' && state.sortOrder === 'asc' ? 'active' : ''} />
                  <ArrowDownwardIcon className={state.sortBy === 'createdAt' && state.sortOrder === 'desc' ? 'active' : ''} />
                </span>
              </th>
              {/* Actions column - standalone /sites catalog only */}
              {showSiteActionsColumn && canSite('sites.archive') && <th className="actions-cell">Actions</th>}
              {showSiteActionsColumn && isPublisher && <th className="actions-cell">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={
                  isPublisher ? 13 :
                  (fromPlanning && !selectionMode ? (isSuperAdminFallback ? 13 : (isAdminFallback ? 11 : 10)) :
                  (isSuperAdminFallback ? (urlProjectId ? 15 : 16) : (isAdminFallback ? 14 : 13)))
                } className="loading-row">
                  Loading...
                </td>
              </tr>
            ) : sites.length === 0 ? (
              <tr>
                <td colSpan={
                  isPublisher ? 13 :
                  (fromPlanning && !selectionMode ? (isSuperAdminFallback ? 13 : (isAdminFallback ? 11 : 10)) :
                  (isSuperAdminFallback ? (urlProjectId ? 15 : 16) : (isAdminFallback ? 14 : 13)))
                } className="empty-row">
                  No guest blog sites found
                </td>
              </tr>
            ) : (
              sites.map((site: GuestBlogSite) => (
                <tr key={site.id}>
                  {/* Checkbox cell in selection mode */}
                  {selectionMode && (
                    <td className="checkbox-cell">
                      <button
                        className="action-btn"
                        onClick={() => handleToggleSite(site.id)}
                        title={selectedSites.has(site.id) ? "Deselect" : "Select"}
                      >
                        {selectedSites.has(site.id) ? <CheckBoxIcon style={{color:'#2563EB'}} /> : <CheckBoxOutlineBlankIcon />}
                      </button>
                    </td>
                  )}
                  {/* Edit cell - moved before Site URL */}
                  {(canSite('sites.edit') || isPublisher) && !fromPlanning && (
                    <td className="actions-cell">
                      <button
                        className="action-btn"
                        onClick={() => handleEdit(site)}
                        title="Edit Site"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" style={{ color: 'var(--text-muted)' }}>
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                    </td>
                  )}
                  {/* Plan Actions cell when viewing a plan - moved to first position */}
                  {fromPlanning && !selectionMode && (
                    <td className="actions-cell">
                      <button
                        className="action-btn delete"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to remove this site from the plan?')) {
                            removeSiteFromPlanMutation.mutate({
                              planId: urlPlanId,
                              siteId: site.id
                            });
                          }
                        }}
                        title="Remove from Plan"
                      >
                        <DeleteIcon />
                      </button>
                    </td>
                  )}
                  <td className="site-url-cell">
                    <div style={{ display: 'flex', alignItems: 'top', gap: '8px' }}>
                      <div>
                        <a 
                          href={site.site_url.startsWith('http') ? site.site_url : `https://${site.site_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="site-url-link"
                          title={site.site_url}
                        >
                          {site.site_url}
                        </a>
                        {/* Show badge if site is already in the plan (selection mode or view mode) */}
                        <div className="site-badges">
                          {selectionMode && existingPlanSitesData?.data?.some((planSite: any) => planSite.guestBlogSiteId === site.id) && (
                            <span className="site-badge in-plan">In Plan</span>
                          )}
                          {fromPlanning && existingPlanSitesData?.data?.some((planSite: any) => planSite.guestBlogSiteId === site.id) && (
                            <span className="site-badge in-plan">Already in Plan</span>
                          )}
                          {/* state.projectId (not urlProjectId): in the /sites
                              browse mode the selected project lives in local
                              state only, and the badge must still show there. */}
                          {state.projectId && !isClient &&
                            (site as any).usageStats?.usedInCurrentProject && (
                            <span className="site-badge ordered">Already Ordered</span>
                          )}
                        </div>
                      </div>
                      {/* Site URL Status Indicator — admin / super admin only */}
                      {isAdminFallback && site.siteStatusCode !== null && site.siteStatusCode !== undefined && (
                        <span className="small-text-live-check"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '15px',
                            height: '15px',
                            borderRadius: '50%',
                            backgroundColor: isGuestBlogSiteHealthOk(site) ? '#10b981' : '#ef4444',
                            color: 'white',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            flexShrink: 0,
                            marginTop: '5px'
                          }}
                          title={guestBlogSiteHealthTooltip(site)}
                        >
                          {isGuestBlogSiteHealthOk(site) ? '✓' : '✕'}
                        </span>
                      )}
                    </div>
                  </td>
                  {isColumnVisible('cart') && showCartColumn && (() => {
                    const cartLine = getCartItemForSite(site.id);
                    const inCart = Boolean(cartLine);
                    const cannotAddToCart = site.status !== 'ACTIVE' || !urlProjectId;
                    const uploadDisabled =
                      cannotAddToCart || rowCartUploadingSiteId === site.id || removingCartSiteId === site.id;
                    const docUrl = cartLine?.contentDocUrl || undefined;
                    const contentUrl = cartLine?.contentUrl || undefined;
                    if (urlProjectId) {
                      // Workflow "Explore Sites": plain Add / Added text button
                      // (Google Doc link is set later in Place Order).
                      const busy = rowCartUploadingSiteId === site.id || removingCartSiteId === site.id;
                      return (
                        <td style={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 32,
                            }}
                          >
                            {inCart ? (
                              <Tooltip title="Click to remove from order">
                                <span>
                                  <Button
                                    size="small"
                                    onClick={() => handleCartRowSelectToggle(site)}
                                    disabled={removingCartSiteId === site.id}
                                    startIcon={<CheckIcon sx={{ fontSize: '16px !important' }} />}
                                    sx={{
                                      minWidth: 78,
                                      textTransform: 'none',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      borderRadius: '7px',
                                      px: 1.25,
                                      py: 0.4,
                                      color: '#2563EB',
                                      border: '1px solid #93C5FD',
                                      backgroundColor: '#EFF6FF',
                                      '&:hover': { backgroundColor: '#DBEAFE', borderColor: '#60A5FA' },
                                    }}
                                  >
                                    Added
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title={
                                  site.status !== 'ACTIVE'
                                    ? 'Site is inactive - cannot add'
                                    : 'Add this site to order'
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    onClick={() => handleQuickAddToCart(site)}
                                    disabled={uploadDisabled}
                                    startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                                    sx={{
                                      minWidth: 78,
                                      textTransform: 'none',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      borderRadius: '7px',
                                      px: 1.25,
                                      py: 0.4,
                                      color: '#FFFFFF',
                                      backgroundColor: '#2563EB',
                                      '&:hover': { backgroundColor: '#1D4ED8' },
                                      '&.Mui-disabled': { backgroundColor: '#E5E7EB', color: '#9CA3AF' },
                                    }}
                                  >
                                    {busy ? '…' : 'Add'}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </td>
                      );
                    }
                    if (isClient) {
                      return (
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="cart-btn"
                            onClick={() => handleAddToCart(site)}
                            disabled={
                              site.status !== 'ACTIVE' ||
                              !urlProjectId ||
                              inCart
                            }
                            title={
                              site.status !== 'ACTIVE'
                                ? 'Site is inactive - cannot add to cart'
                                : inCart
                                  ? 'Already in cart for this project'
                                  : urlProjectId
                                    ? 'Add to Cart'
                                    : 'Select project first'
                            }
                          >
                            <CartIcon />
                          </button>
                        </td>
                      );
                    }
                    return (
                      <td style={{ textAlign: 'center' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.75,
                            minHeight: 32,
                          }}
                        >
                          {inCart && (
                            <Tooltip title="Remove from cart">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCartRowSelectToggle(site)}
                                  disabled={removingCartSiteId === site.id}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '7px',
                                    border: '1px solid #93C5FD',
                                    backgroundColor: '#EFF6FF',
                                    '&:hover': {
                                      backgroundColor: '#DBEAFE',
                                      borderColor: '#60A5FA',
                                    },
                                  }}
                                >
                                  <CheckBoxIcon sx={{ color: '#2563EB', fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={contentUrl ? 'Update Google Doc URL' : 'Paste Google Doc URL'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={uploadDisabled}
                                onClick={() => handleRowCartContentUrlSet(site)}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '7px',
                                  border: contentUrl ? '1px solid #C7D2FE' : '1px solid #E5E7EB',
                                  backgroundColor: contentUrl ? '#EEF2FF' : '#FFFFFF',
                                  color: contentUrl ? '#4F46E5' : '#64748B',
                                  '&:hover': {
                                    backgroundColor: contentUrl ? '#E0E7FF' : '#F8FAFC',
                                    borderColor: contentUrl ? '#A5B4FC' : '#CBD5E1',
                                    color: contentUrl ? '#4338CA' : '#334155',
                                  },
                                }}
                              >
                                <LinkIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {docUrl ? (
                            <Tooltip title="View document">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setRowCartDocPreview({
                                    url: docUrl,
                                    name: guestBlogOrderService.getFileName(docUrl),
                                  })
                                }
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '7px',
                                  border: '1px solid #BFDBFE',
                                  backgroundColor: '#EFF6FF',
                                  color: '#2563EB',
                                  '&:hover': {
                                    backgroundColor: '#DBEAFE',
                                    borderColor: '#93C5FD',
                                  },
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : contentUrl ? (
                            <Tooltip title="Open content URL">
                              <IconButton
                                size="small"
                                onClick={() => window.open(contentUrl, '_blank', 'noopener,noreferrer')}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '7px',
                                  border: '1px solid #BFDBFE',
                                  backgroundColor: '#EFF6FF',
                                  color: '#2563EB',
                                  '&:hover': {
                                    backgroundColor: '#DBEAFE',
                                    borderColor: '#93C5FD',
                                  },
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </td>
                    );
                  })()}
                  {/* Usage Stats cell when projectId present and not in selection mode */}
                  {state.projectId && !selectionMode && (
                    <td>
                      {(site as any).usageStats && (() => {
                        // Client-wide monthly count (all projects + cart items this month).
                        const monthlyCount = (site as any).usageStats.monthlyCount as number;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className={`usage-chip ${
                              monthlyCount === 0 ? 'zero' :
                              monthlyCount <= 2 ? 'low' :
                              monthlyCount <= 5 ? 'medium' : 'high'
                            }`}>
                              <RepeatIcon style={{ width: '12px', height: '12px' }} />
                              {monthlyCount}x
                            </span>
                            {(site as any).usageStats.usedInCurrentProject && (
                              <span title="Already used in this project">
                                <WarningIcon style={{ fontSize: '16px', color: '#F59E0B' }} />
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  )}
                  {isColumnVisible('publisher') && (isAdminFallback || isSuperAdminFallback) && (
                    <td>
                      {site.publisher ? site.publisher.publisherName : 'N/A'}
                    </td>
                  )}
                  {isColumnVisible('outreachEmail') && canSite('sites.column.outreach_email') && (
                    <td>{site.outreach_email || 'N/A'}</td>
                  )}
                  {isColumnVisible('da') && <td>{site.da}</td>}
                  {isColumnVisible('dr') && <td>{site.dr}</td>}
                  {isColumnVisible('traffic') && <td>{guestBlogSiteService.formatTraffic(site.ahrefs_traffic)}</td>}
                  {isColumnVisible('gbBasePrice') && (isSuperAdminFallback || isPublisher || canSite('sites.column.base_prices')) && (
                    <td className="price-cell">
                      {formatRoundedPrice(site.base_price)}
                    </td>
                  )}
                  {isColumnVisible('liBasePrice') && (isSuperAdminFallback || isPublisher || canSite('sites.column.base_prices')) && (
                    <td className="price-cell">
                      {site.li_base_price ? formatRoundedPrice(site.li_base_price) : '-'}
                    </td>
                  )}
                  {isColumnVisible('gbPrice') && !isPublisher && (
                    <td className="price-cell">
                      <div>
                        <span className="price-display">{formatRoundedPrice(site.displayed_price)}</span>
                        {canSite('sites.price_override') && site.isOverride && (
                          <span className="site-badge override">Override</span>
                        )}
                      </div>
                    </td>
                  )}
                  {isColumnVisible('liPrice') && !isPublisher && (
                    <td className="price-cell">
                      <span className="price-secondary">
                        {site.li_displayed_price ? formatRoundedPrice(site.li_displayed_price) : '-'}
                      </span>
                    </td>
                  )}
                  {isColumnVisible('ss') && <td>{site.ss || 'N/A'}</td>}
                  {isColumnVisible('keywords') && <td>{(site as any).keywords || 'N/A'}</td>}
                  {isColumnVisible('category') && <td>{site.categoryDisplay}</td>}
                  {isColumnVisible('country') && <td>{site.country}</td>}
                  {isColumnVisible('language') && <td>{site.site_language}</td>}
                  {isColumnVisible('tat') && <td>{site.tat}</td>}
                  {isColumnVisible('status') && (
                    <td>
                      <span className={`status-chip ${site.status === GuestBlogSiteStatus.ACTIVE ? 'active' : 'inactive'}`}>
                        {site.status === GuestBlogSiteStatus.ACTIVE ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  )}
                  <td>
                    {new Date(site.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  {/* Other actions at the end — standalone /sites only */}
                  {showSiteActionsColumn && canSite('sites.archive') && (
                    <td className="actions-cell">
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button
                          className="action-btn archive"
                          onClick={() => handleDelete(site)}
                          title="Archive Site"
                        >
                          <ArchiveIcon />
                        </button>
                      </div>
                    </td>
                  )}
                  {showSiteActionsColumn && isPublisher && (
                    <td className="actions-cell">
                      {(() => {
                        const existingReq = priceChangeRequestsBySite[site.id];
                        if (!existingReq) {
                          // No existing request - show Request icon button
                          return (
                            <button
                              className="price-change-btn"
                              onClick={() => {
                                setSelectedSiteForPriceChange(site);
                                setPriceChangeModalOpen(true);
                              }}
                              title="Request Price Change"
                            >
                              <AttachMoneyIcon />
                            </button>
                          );
                        }
                        // Has existing request - show status + action
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span className={`price-change-status ${existingReq.status.toLowerCase()}`}>
                              {existingReq.status === 'PENDING' 
                                ? `Pending ($${existingReq.requestedPrice})` 
                                : existingReq.status === 'APPROVED'
                                ? `Approved ($${existingReq.requestedPrice})`
                                : `Rejected ($${existingReq.requestedPrice})`
                              }
                            </span>
                            {existingReq.rejectionReason && existingReq.status === 'REJECTED' && (
                              <span title={`Reason: ${existingReq.rejectionReason}`} style={{ fontSize: '10px', color: '#EF4444', maxWidth: '140px', textAlign: 'center', cursor: 'help', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {existingReq.rejectionReason}
                              </span>
                            )}
                            <button
                              className={`price-change-btn ${existingReq.status === 'REJECTED' ? 'rejected' : ''}`}
                              onClick={() => {
                                setSelectedSiteForPriceChange(site);
                                setPriceChangeModalOpen(true);
                              }}
                              title={
                                existingReq.status === 'PENDING' 
                                  ? 'Modify Request' 
                                  : existingReq.status === 'REJECTED'
                                  ? 'Request Again'
                                  : 'Request New Change'
                              }
                            >
                              {existingReq.status === 'PENDING' 
                                ? <EditIcon style={{ fontSize: '14px' }} /> 
                                : existingReq.status === 'REJECTED'
                                ? <RefreshIcon style={{ fontSize: '14px' }} />
                                : <AttachMoneyIcon style={{ fontSize: '14px' }} /> }
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={state.rowsPerPage}
          page={state.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}

      {/* Bottom-left "Go to order" so users can jump to Create Order without scrolling up */}
      {inWorkflow && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16 }}>
          <button
            type="button"
            onClick={() => workflow?.goToStep('plan')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            Go to order →
          </button>
        </div>
      )}

      {/* Guest Blog Site Form Dialog - Only for Admin/Super Admin */}
      {!isPublisher && !isClient && (
        <GuestBlogSiteForm
          open={formOpen}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
          site={selectedSite}
          loading={createMutation.isPending || updateMutation.isPending}
          isAdmin={isAdminFallback}
          selectedClientId={state.clientId || undefined}
          selectedClientPercentage={
            (() => {
              const c = clientsData?.data?.find((cl: { id: string; percentage?: number }) => cl.id === state.clientId);
              if (!c) return isAgency ? 0 : 100;
              return c.percentage ?? (isAgency ? 0 : 100);
            })()
          }
        />
      )}

      {/* Bulk Upload Modal - Only for Admin/Super Admin */}
      {!isPublisher && !isClient && (
        <BulkUploadModal
          isOpen={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onSuccess={(message) => {
            queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
            setBulkUploadOpen(false);
            setSnackbar({
              open: true,
              message: message || 'Bulk upload completed successfully!',
              severity: 'success',
            });
          }}
        />
      )}

      {rowCartDocPreview && (
        <DocumentPreviewModal
          open={Boolean(rowCartDocPreview)}
          onClose={() => setRowCartDocPreview(null)}
          documentUrl={rowCartDocPreview.url}
          fileName={rowCartDocPreview.name}
        />
      )}

      <Dialog
        open={contentUrlDialogOpen}
        onClose={() => {
          if (rowCartUploadingSiteId) return;
          setContentUrlDialogOpen(false);
          setContentUrlDialogSite(null);
          setContentUrlInput('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Paste Google Doc URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="https://docs.google.com/document/d/..."
            value={contentUrlInput}
            onChange={(e) => setContentUrlInput(e.target.value)}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setContentUrlDialogOpen(false);
              setContentUrlDialogSite(null);
              setContentUrlInput('');
            }}
            disabled={Boolean(rowCartUploadingSiteId)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRowCartContentUrlSave}
            disabled={Boolean(rowCartUploadingSiteId)}
          >
            {rowCartUploadingSiteId ? 'Saving...' : 'Save URL'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cart Item Form */}
      {selectedSiteForCart && (
        <CartItemForm
          open={cartPopupOpen}
          onClose={() => {
            setCartPopupOpen(false);
            setSelectedSiteForCart(null);
          }}
          onSubmit={addItemToCart}
          siteUrl={selectedSiteForCart.site_url}
          clientName={clients.find(c => c.id === state.clientId)?.name || ''}
          adjustedPrice={selectedSiteForCart.displayed_price}
          liPrice={selectedSiteForCart.li_displayed_price}
        />
      )}

      {/* Cart Sidebar - floating cart drawer */}
      {urlProjectId && cartData && (
        <CartSidebar
          projectId={urlProjectId}
          projectName={cartData.projectName || 'Project'}
          clientName={cartData.clientName || 'Client'}
          open={cartSidebarOpen}
          onClose={() => setCartSidebarOpen(false)}
          onCheckout={() => {
            setCartSidebarOpen(false);
            setCheckoutModalOpen(true);
          }}
        />
      )}

      {/* Checkout Modal - works for both project context and AllCartDrawer checkout */}
      {(urlProjectId || checkoutProjectId) && (checkoutModalOpen) && (
        <CheckoutModal
          open={checkoutModalOpen}
          onClose={() => {
            setCheckoutModalOpen(false);
            setCheckoutProjectId('');
            setCheckoutProjectName('');
            setCheckoutClientName('');
            queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
            // Refresh all cart data if checking out from AllCartDrawer
            if (!urlProjectId && checkoutProjectId) {
              const refreshCart = async () => {
                try {
                  const response = await cartService.getAllCartItems();
                  setAllCartData(response.data);
                } catch (error) {
                  console.error('Error refreshing cart:', error);
                }
              };
              refreshCart();
            }
          }}
          projectId={urlProjectId || checkoutProjectId}
          projectName={cartData?.projectName || checkoutProjectName || 'Project'}
          clientName={cartData?.clientName || checkoutClientName || 'Client'}
        />
      )}

      {/* All Cart Drawer - shows all cart items grouped by project when no project context */}
      {!urlProjectId && allCartData && (
        <AllCartDrawer
          open={allCartDrawerOpen}
          onClose={() => setAllCartDrawerOpen(false)}
          projects={allCartData.projects || []}
          totalItems={allCartData.totalItems || 0}
          totalAmount={allCartData.totalAmount || 0}
          onRemoveItem={async (itemId) => {
            try {
              await cartService.removeFromCart(itemId);
              const response = await cartService.getAllCartItems();
              setAllCartData(response.data);
              queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
              setSnackbar({
                open: true,
                message: 'Cart item removed successfully.',
                severity: 'success',
              });
            } catch (error: any) {
              setSnackbar({
                open: true,
                message: error?.response?.data?.error || 'Failed to remove cart item.',
                severity: 'error',
              });
            }
          }}
          onCheckout={(projectId, projectName, clientName) => {
            setCheckoutProjectId(projectId);
            setCheckoutProjectName(projectName);
            setCheckoutClientName(clientName);
            setAllCartDrawerOpen(false);
            setCheckoutModalOpen(true);
          }}
          onRefresh={async () => {
            setAllCartLoading(true);
            try {
              const response = await cartService.getAllCartItems();
              setAllCartData(response.data);
            } catch (error) {
              console.error('Error refreshing cart:', error);
            } finally {
              setAllCartLoading(false);
            }
          }}
        />
      )}

      {/* Request Price Change Modal */}
      {selectedSiteForPriceChange && (
        <RequestPriceChangeModal
          open={priceChangeModalOpen}
          onClose={() => {
            setPriceChangeModalOpen(false);
            setSelectedSiteForPriceChange(null);
          }}
          site={{
            id: selectedSiteForPriceChange.id,
            site_url: selectedSiteForPriceChange.site_url,
            base_price: selectedSiteForPriceChange.base_price,
          }}
          existingRequest={priceChangeRequestsBySite[selectedSiteForPriceChange.id] || null}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default GuestBlogSitesPage;